import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, Eraser } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SpaceSection } from "@/components/SpaceSection";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export default function ShoppingPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: items = [] } = useQuery({
    queryKey: ["shopping", id],
    queryFn: async () => (await api.get(`/workspaces/${id}/shopping`)).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["shopping", id] });
  const pending = items.filter((i) => !i.checked);
  const done = items.filter((i) => i.checked);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post(`/workspaces/${id}/shopping`, { name, quantity: qty });
      setName(""); setQty("");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/shopping/${item.id}`, { checked: !item.checked });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/shopping/${item.id}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const clearChecked = async () => {
    try {
      const { data } = await api.post(`/workspaces/${id}/shopping/clear-checked`);
      toast.success(`${data.deleted} ${data.deleted === 1 ? "artículo eliminado" : "artículos eliminados"}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const Row = ({ item }) => (
    <div className="p-3 flex items-center gap-3" data-testid={`shopping-item-${item.id}`}>
      <Checkbox checked={item.checked} onCheckedChange={() => toggle(item)} data-testid={`shopping-check-${item.id}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${item.checked ? "line-through text-muted-foreground" : ""}`}>
          {item.name}{item.quantity && <span className="text-muted-foreground font-normal"> · {item.quantity}</span>}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          añadido por {item.added_by_name}{item.checked && item.checked_by_name ? ` · ✓ ${item.checked_by_name}` : ""}
        </p>
      </div>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(item)}
        data-testid={`shopping-delete-${item.id}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );

  return (
    <SpaceSection title="Lista de la compra">
      <div className="max-w-2xl space-y-4" data-testid="shopping-page">
        <div className="flex gap-2">
          <Input data-testid="shopping-name-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Añadir artículo… (ej: Leche)" maxLength={200} />
          <Input data-testid="shopping-qty-input" value={qty} onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Cant." maxLength={50} className="w-24" />
          <Button onClick={add} disabled={adding || !name.trim()} data-testid="shopping-add-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {items.length === 0 ? (
          <Card className="p-10 text-center border-dashed" data-testid="shopping-empty">
            <ShoppingCart className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">La lista está vacía. Añade lo que haga falta comprar.</p>
          </Card>
        ) : (
          <>
            {pending.length > 0 && (
              <Card className="divide-y divide-border/60" data-testid="shopping-pending-list">
                {pending.map((i) => <Row key={i.id} item={i} />)}
              </Card>
            )}
            {done.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Comprado ({done.length})</p>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearChecked} data-testid="shopping-clear-btn">
                    <Eraser className="h-3.5 w-3.5 mr-1" /> Vaciar comprados
                  </Button>
                </div>
                <Card className="divide-y divide-border/60 opacity-70" data-testid="shopping-done-list">
                  {done.map((i) => <Row key={i.id} item={i} />)}
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </SpaceSection>
  );
}
